import { useState, useContext } from "react"
import { v4 } from "uuid"
import { FirebaseContext } from "../Firebase"
import { sendStandardToast } from "../toasts/standard-toast.js";

const fileTypesWithThumbnails = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
])

/**
 * @typedef {Object} userFile
 * @property {string} fileId
 * @property {string} userId
 * @property {string} fileType
 * @property {boolean} isSaved
 * @property {boolean} isLoading
 * @property {URL} fileURL
 * @property {URL} thumbnailURL
 * @property {File} fileData
 * @property {File} thumbnailData
 * @property {string} errorMessage
 */

/**
 * Interface for working with user-uploaded files on the front-end
 * 
 * @param {string} userId - defaults to the current user's id if undefined
 * @param {string} purpose - Ex: "storyboard"
 * @param {string[]} fileIds - ids of all files to be downloaded
 * 
 * @returns {userFile[]} filesList - fileObjects corresponding to fileIds
 * @returns {function} addFile - converts a Javascript File object to a userFile 
 * object. returns a new fileId corresponding to the new userFile object 
 */
const useUserFiles = (userId, purpose, fileIds) => {
  const { firebase } = useContext(FirebaseContext)
  const [filesData, setFilesData] = useState({})

  const fetchFile = async (fileId) => {
    const fileType = await firebase.getUserFileType(userId, purpose, fileId)
    const fileURL = await firebase.getUserFileURL(userId, purpose, fileId)
    const fetchedFile = {
      fileId: fileId,
      userId: userId || firebase.auth.currentUser.uid,
      fileType: fileType,
      isSaved: true,
      fileURL: fileURL,
    }
    if (!fileType || !fileURL) {
      fetchedFile.errorMessage = "Error when attempting to download file from server"
    } else if (fileTypesWithThumbnails.has(fileType)) {
      fetchedFile.thumbnailURL = await firebase.getUserFileThumbnailURL(userId, purpose, fileId)
    }
    setFilesData(prevData => ({...prevData, [fileId]: fetchedFile}))
  }

  const generateThumbnail = async (file, fileId) => {
    let thumbnailFile = undefined
    try {
      thumbnailFile = await firebase.generateFileThumbnail(file)
    } catch (e) {
      console.log(`Failed to generate thumbnail for ${file.name}`, e)
      sendStandardToast({
        heading: `Failed to generate thumbnail for ${file.name}`,
        message: e.message,
      })
    }

    const updatedFile = {
      fileId: fileId,
      userId: userId || firebase.auth.currentUser.uid,
      fileType: file.type,
      isSaved: false,
      isLoading: false,
      fileURL: URL.createObjectURL(file),
      fileData: file,
      thumbnailURL: thumbnailFile && URL.createObjectURL(thumbnailFile),
      thumbnailData: thumbnailFile,
    }
    setFilesData(prevData => ({...prevData, [fileId]: updatedFile}))
  }

  const saveFile = async (userFile) => {
    try {
      await firebase.uploadUserFile(purpose, userFile.fileId, userFile.fileData, null)
      const fileURL = await firebase.getUserFileURL(null, purpose, userFile.fileId)
      const updatedFile = {
        ...userFile,
        isSaved: true,
        isLoading: false,
        fileURL: fileURL,
      }
      setFilesData(prevData => ({...prevData, [userFile.fileId]: updatedFile}))
    } catch (e) {
      const fileWithError = {
        ...userFile,
        isLoading: false,
        errorMessage: `failed to upload file: ${e}`,
      }
      setFilesData(prevData => ({...prevData, [userFile.fileId]: fileWithError}))
    }
  }

  /**
   * Process a file uploaded by a user so that other components can use it
   * @param {File} file - Javascript file object to 
   * @param {boolean} commitFile - Whether the file should be immediately uploaded
   * to firebase storage (This doesn't currently support files with generated
   * thumbnails being committed, but it could be extended to do so)
   * @returns {string} ID of the file
   */
  const addFile = (file, commitFile) => {
    const newId = v4()
    // don't generate thumbnails for files larger than 30 MB (approaches limit
    // of max request size for google cloud)
    const needsThumbnail = fileTypesWithThumbnails.has(file.type) && file.size <= 30000000
    const newFile = {
      fileId: newId,
      userId: userId || firebase.auth.currentUser.uid,
      fileType: file.type,
      isSaved: false,
      isLoading: commitFile || needsThumbnail,
      fileURL: commitFile ? null : URL.createObjectURL(file),
      fileData: file,
    }
    setFilesData(prevData => ({...prevData, [newId]: newFile}))

    if (needsThumbnail) {
      generateThumbnail(file, newId)
    } else if (commitFile) {
      saveFile(newFile)
    }
    return newId
  }

  const filesList = fileIds.map(id => {
    if (filesData[id]) {
      return filesData[id]
    } else {
      const placeholderFile = {fileId: id}
      setFilesData(prevData => ({...prevData, [id]: placeholderFile}))
      fetchFile(id)
      return placeholderFile
    }
  })

  return {
    filesList,
    addFile,
  }
}

export default useUserFiles
